import { APP_BASE_HREF } from "@angular/common";
import {
	APP_ID,
	APP_INITIALIZER,
	CUSTOM_ELEMENTS_SCHEMA,
	ErrorHandler,
	InjectionToken,
	LOCALE_ID,
	NgModule,
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatDialogModule } from "@angular/material/dialog";
import {
	LegacyRippleGlobalOptions as RippleGlobalOptions,
	MAT_LEGACY_RIPPLE_GLOBAL_OPTIONS as MAT_RIPPLE_GLOBAL_OPTIONS,
} from "@angular/material/legacy-core";
import {
	MAT_LEGACY_DIALOG_DEFAULT_OPTIONS as MAT_DIALOG_DEFAULT_OPTIONS,
	MatLegacyDialogConfig as MatDialogConfig,
} from "@angular/material/legacy-dialog";
import { provideClientHydration, withNoHttpTransferCache } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ROUTES, UrlSerializer } from "@angular/router";
import { EffectsModule } from "@ngrx/effects";
import {
	Action,
	ActionReducer,
	ActionReducerMap,
	INITIAL_STATE,
	select,
	Store,
	StoreModule,
	USER_PROVIDED_META_REDUCERS,
} from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";
import { HomeWrapperModule } from "@obg/common.home";
import { LazyLoaderModule } from "@obg/common.lazy-loader";
import { MatCommonLimitsReminderModule } from "@obg/common.limits-reminder";
import { MatCommonLoginModule } from "@obg/common.login";
import { RealtimeNotificationsModule } from "@obg/common.realtime-notifications";
import { RegistrationConfigService } from "@obg/common.registration/sdk";
import { CommunicationSystemModule } from "@obg/core.communication-system";
import { _provideRevealContentKeysExperiment } from "@obg/core.debug";
import { ExperimentConfigService, ExperimentState } from "@obg/core.experiment";
import { ZHH_REALITY_CHECK_SERVICE } from "@obg/core.injection";
import { AamsGameDataModule } from "@obg/gaming.aams-game-data";
import { MatCommonModule, MatCustomerAssessmentsDialogService, SplashScreenModule } from "@obg/mat.ui.common";
import { ZhhRealityCheckService } from "@obg/mat.ui.common.responsible-gaming";
import {
	DialogService as MatDialogService,
	MatCoreModule,
	MatKycMessageService,
	TranslatableAlertService,
	TranslatableSnackBarService,
} from "@obg/mat.ui.core";
import { enableBatching, NGRX_UTILS_REDUCER, NgrxUtilsModule, stateTransfer } from "@obg/ngrx-utils";
import {
	COMMON_REDUCER,
	CommonModule as NgxCommonModule,
	CommonState,
	CustomerAssessmentsDialogService,
} from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	AppContextSelector,
	AppContextState,
	AppStartupContext,
	CORE_CONFIG,
	CORE_REDUCER,
	CORE_ROUTE,
	CoreConfig,
	CoreModule,
	CoreState,
	CURRENT_LOGIN_SOURCE,
	CurrentLoginSource,
	DialogService,
	Jurisdiction,
	JURISDICTION_CONFIG_PREFIX,
	JurisdictionSelector,
	KycMessageService,
	LoadingService,
	LoginSource,
	ProductType,
	STARTUP_CONTEXT_TOKEN,
} from "@obg/ngx.core";
import { CORE_CONTENT_REDUCER, CoreContentModule, CoreContentState } from "@obg/ngx.core.content";
import {
	CONFIG_SERVICE_ENABLE_PREPROCESSOR_INJECTION_TOKEN,
	ConfigService,
	DeviceExperience,
	DeviceType,
	getActiveDimensions,
	GLOBAL_CONFIG,
	GlobalConfig,
	GlobalModule,
	LoadingService as GlobalLoadingService,
	LoggingConfigService,
	OPTIMIZELY_FEATURE_FLAGS,
} from "@obg/ngx.global";
import {
	DocumentRef,
	GlobalExceptionHandler,
	HOOKABLE_ROUTES,
	HookableRouterModule,
	LoggingService,
	PlatformService,
} from "@obg/ngx.global.evo";
import { SentryLoggingProvider } from "@obg/ngx.global.logging.sentry";
import { SignalrModule } from "@obg/ngx.real-time.signalr";
import {
	LowerCaseUrlSerializer,
	ObgPreloadingStrategy,
	ROUTER_CONFIG,
	RouterConfig,
	RouterModule,
	ROUTING_REDUCER,
} from "@obg/ngx.router";
import { CoreUiModule } from "@obg/ngx.ui.core";
import { ErrorsModule } from "@obg/ngx.ui.core/errors";
import { GlobalUiModule } from "@obg/ngx.ui.global";
import { ignoreFalsy } from "@obg/rxjs";
import { cloneDeep, merge, toLower, uniq } from "lodash-es";
import { storeLogger } from "ngrx-store-logger";
import { BehaviorSubject, Subscription } from "rxjs";
import { filter } from "rxjs/operators";

import { AppComponent } from "./app.component";
import { AppEffect } from "./app.effect";
import { registerAllLocaleData } from "./app.locales";
import { createRoutes } from "./app.routes";
import { CoreLayoutModule } from "./core/layouts/core-layout/core-layout.module";
import { EmptyLayoutModule } from "./core/layouts/empty-layout/empty-layout.module";
import { ChannelNotSupportedComponent } from "./errors/channel-not-supported/channel-not-supported.component";
import { PlatformNotSupportedComponent } from "./errors/platform-not-supported/platform-not-supported.component";

export const APP_REDUCER_TOKEN = new InjectionToken<ActionReducerMap<CoreContentState>>("appReducer");

const globalRippleConfig: RippleGlobalOptions = {
	disabled: true,
};

const _optimizelyFeatureFlagsFactoryFn = _optimizelyFeatureFlagsFactory();

@NgModule({
	declarations: [AppComponent, PlatformNotSupportedComponent, ChannelNotSupportedComponent],
	imports: [
		BrowserAnimationsModule,
		HookableRouterModule.forRoot(undefined, {
			initialNavigation: "enabledNonBlocking",
			preloadingStrategy: ObgPreloadingStrategy,
		}),
		StoreModule.forRoot(APP_REDUCER_TOKEN, {
			runtimeChecks: {
				strictStateImmutability: false,
				strictActionImmutability: false,
			},
		}),
		EffectsModule.forRoot([AppEffect]),
		TranslateModule.forRoot(),
		GlobalModule.forRoot(),
		NgrxUtilsModule.forRoot<CommonState>({
			keysToIgnore: [
				"route",
				"appContext",
				"auth",
				"cookieNotice",
				"cookieConsent",
				"sportsbook",
				"fetchStatus",
				"stateCache",
			],
		}),
		RouterModule.forRoot(),
		CoreModule.forRoot(),
		CoreContentModule.forRoot(),
		CoreUiModule.forRoot(),
		// TODO: can be removed once all brands are migrated to B2B
		GlobalUiModule.forRoot(),
		ErrorsModule.forRoot({
			internalError: {
				useAssetsPath: true,
			},
		}),
		NgxCommonModule.forRoot(),
		MatCoreModule,
		CoreLayoutModule,
		EmptyLayoutModule,
		MatCommonModule,
		MatCommonLoginModule,
		HomeWrapperModule,
		// todo: Remove the below modules when removing the test login component from within `core.component`
		FormsModule,
		ReactiveFormsModule,
		RealtimeNotificationsModule.forRoot(),
		LazyLoaderModule,
		MatCommonLimitsReminderModule,
		SplashScreenModule,
		CommunicationSystemModule,
		SignalrModule.forRoot(),
		AamsGameDataModule,
		MatDialogModule,
	],
	providers: [
		// without disabling the http transfer cache angular indiscriminately caches GET requests
		// that basically breaks all polling get requests
		// feel free to check if this has changed
		...(!globalThis.isDOMSSR ? [provideClientHydration(withNoHttpTransferCache())] : []),
		{ provide: APP_ID, useValue: "halo-app" },
		{
			provide: HOOKABLE_ROUTES,
			useFactory: createRoutes,
			deps: [ExperimentConfigService, RegistrationConfigService],
		},
		{ provide: ErrorHandler, useClass: GlobalExceptionHandler },
		{ provide: CONFIG_SERVICE_ENABLE_PREPROCESSOR_INJECTION_TOKEN, useValue: true },
		{ provide: LOCALE_ID, useFactory: _localeId, deps: [STARTUP_CONTEXT_TOKEN] },
		{ provide: APP_BASE_HREF, useFactory: _appBaseHref, deps: [STARTUP_CONTEXT_TOKEN] },
		{
			provide: APP_INITIALIZER,
			useFactory: _registerAllLocaleData,
			multi: true,
		},
		{ provide: APP_REDUCER_TOKEN, useFactory: _appReducerFactory },
		{ provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
		{ provide: GLOBAL_CONFIG, useFactory: _globalConfigFactory, deps: [STARTUP_CONTEXT_TOKEN, PlatformService] },
		{
			provide: OPTIMIZELY_FEATURE_FLAGS,
			useFactory: _optimizelyFeatureFlagsFactoryFn,
			deps: [STARTUP_CONTEXT_TOKEN],
		},
		{ provide: ROUTER_CONFIG, useFactory: _routerConfigFactory, deps: [STARTUP_CONTEXT_TOKEN, PlatformService] },
		{ provide: CORE_CONFIG, useFactory: _coreConfigFactory, deps: [STARTUP_CONTEXT_TOKEN] },
		{ provide: INITIAL_STATE, useFactory: _provideInitialState, deps: [STARTUP_CONTEXT_TOKEN, ROUTES] },
		{ provide: CURRENT_LOGIN_SOURCE, useFactory: _provideCurrentLoginSource, deps: [STARTUP_CONTEXT_TOKEN] },
		{
			provide: USER_PROVIDED_META_REDUCERS,
			useFactory: _provideMetaReducers,
			deps: [STARTUP_CONTEXT_TOKEN, PlatformService],
		},
		{ provide: GlobalLoadingService, useClass: LoadingService },
		{
			provide: APP_INITIALIZER,
			useFactory: _registerJurisdictionConfig,
			deps: [STARTUP_CONTEXT_TOKEN, Store, ConfigService],
			multi: true,
		},
		{
			provide: LoggingService,
			useFactory: _loggingService,
			deps: [LoggingConfigService, PlatformService],
		},
		{ provide: DialogService, useExisting: MatDialogService },
		{ provide: AbstractTranslatableSnackBarService, useExisting: TranslatableSnackBarService },
		{ provide: AbstractTranslatableAlertService, useExisting: TranslatableAlertService },
		{ provide: UrlSerializer, useClass: LowerCaseUrlSerializer },
		{ provide: MAT_DIALOG_DEFAULT_OPTIONS, useFactory: _matDialogDefaultOptionsFactory },
		{ provide: KycMessageService, useClass: MatKycMessageService },
		{ provide: CustomerAssessmentsDialogService, useClass: MatCustomerAssessmentsDialogService },
		{ provide: ZHH_REALITY_CHECK_SERVICE, useClass: ZhhRealityCheckService },
	],
	bootstrap: [AppComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
	constructor(
		documentRef: DocumentRef,
		private store: Store<CoreState>
	) {
		this.store
			.select(AppContextSelector.getProduct)
			.pipe(ignoreFalsy())
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe((product) => {
				if (product === ProductType.Sportsbook) {
					documentRef?.native?.body.classList.add(`obg-sb-gen2`);
				} else {
					documentRef?.native?.body.classList.remove(`obg-sb-gen2`);
				}
			});
	}
}

/** @internal */
export function _loggingService(
	loggingConfigService: LoggingConfigService,
	platformService: PlatformService
): LoggingService | SentryLoggingProvider {
	return platformService.isBrowser && loggingConfigService.getSentryEnabled()
		? new SentryLoggingProvider({
				isProd: loggingConfigService.isProdMode(),
				url: loggingConfigService.getSentryUrl(),
				version: loggingConfigService.getVersion(),
				environment: loggingConfigService.getEnvironment(),
				ignoredErrors: loggingConfigService.getIgnoredErrors(),
			})
		: new LoggingService();
}

/** @internal */
export function _registerJurisdictionConfig(
	startupContext: AppStartupContext,
	store: Store<CoreState>,
	configService: ConfigService
) {
	return (): Subscription =>
		store
			.pipe(
				select(JurisdictionSelector.get),
				filter((jurisdiction) => !!jurisdiction)
			)
			.subscribe((jurisdiction) => {
				configService.updateJurisdiction(jurisdiction!.toLowerCase());

				if (startupContext.jurisdictionClientConfiguration) {
					configService.removeItem(JURISDICTION_CONFIG_PREFIX);
					configService.setItems(
						cloneDeep(startupContext!.jurisdictionClientConfiguration![toLower(jurisdiction)])
					);
				}
			});
}

/** @internal */
export function _registerAllLocaleData() {
	return registerAllLocaleData;
}

/**
 * @internal
 */
export function _localeId(startupContext: AppStartupContext): string {
	return startupContext.market.culture;
}

/**
 * @internal
 */
export function _appBaseHref(startupContext: AppStartupContext): string {
	return startupContext.isLanguageCodeInUrlDisabled || startupContext.isCurrentMarketDefaultMarket
		? "/"
		: `/${startupContext.market.urlSlug ?? startupContext.market.languageCode}`;
}

/**
 * @internal
 */
export function _matDialogDefaultOptionsFactory() {
	const defaultConfig = new MatDialogConfig();
	defaultConfig.width = "580px";
	return defaultConfig;
}

/** @internal */
export function _appReducerFactory() {
	return {
		...NGRX_UTILS_REDUCER,
		...ROUTING_REDUCER,
		...CORE_REDUCER,
		...CORE_CONTENT_REDUCER,
		...COMMON_REDUCER,
		// Required to store memoization state during SSR only
		__memoize: (state = new Map()) => state,
	};
}

/** @internal */
export function _provideMetaReducers(startupContext: AppStartupContext, platforService: PlatformService) {
	return [
		enableBatching,
		_provideStoreLogger(startupContext, platforService),
		_provideExperimentState(platforService),
		_provideDebugState(startupContext, platforService),
		_provideRevealContentKeysExperiment(startupContext),
		stateTransfer,
	];
}

/** @internal */
export function _provideStoreLogger({ appContext }: AppStartupContext, platformService: PlatformService) {
	const storeLoggerReducer = storeLogger({ collapsed: true });
	return (reducer: ActionReducer<CoreContentState>) => (state: CoreContentState, action: Action) =>
		platformService.isBrowser && appContext.isDebug && !globalThis.isDOMSSR
			? storeLoggerReducer(reducer)(state, action)
			: reducer(state, action);
}

/** @internal */
export function _provideExperimentState(platformService: PlatformService) {
	return (reducer: ActionReducer<any>) => (state: any, action: Action) => {
		const newState = reducer(state, action);

		if (platformService.isBrowser) {
			// expose specific data to obg experiments
			(window as any).obgExperimentState = {
				isDepositAllowed: newState.customer?.customer?.allowedActions?.deposit?.isAllowed,
				hasReachedDepositThreshold: newState.depositThreshold?.hasReachedThreshold,
				depositLimit: newState.limitsReminder?.limitsSummary?.depositLimitInfoResponse?.remaningValue,
				kycState: newState.customer?.customer?.kyc?.state,
				balance: newState.wallet?.balance?.totalAmount,
				currencyCode: newState.wallet?.balance?.currencyCode,
			} satisfies ExperimentState;
		}

		return newState;
	};
}

/** @internal */
export function _provideDebugState(
	{ appContext, exposeObgState }: AppStartupContext,
	platformService: PlatformService
) {
	return (reducer: ActionReducer<any>) => (state: any, action: Action) => {
		const newState = reducer(state, action);

		// expose state in browser's console for debugging purposes
		if (platformService.isBrowser && (appContext.isDebug || exposeObgState)) {
			(window as any).obgState = newState;
		}

		return newState;
	};
}

/** @internal */
export function _provideInitialState({ appContext }: AppStartupContext) {
	return {
		appContext: {
			productHistory: [],
			...appContext,
		} as AppContextState,
	};
}

// TODO: OBGB-12018 remove bang
/** @internal */
export function _provideCurrentLoginSource({ device }: AppStartupContext): CurrentLoginSource {
	const loginSource = device!.deviceType === DeviceType.Mobile ? LoginSource.Mobile : LoginSource.Web;
	return {
		loginSource,
	};
}

/** @internal */
export function _globalConfigFactory(context: AppStartupContext, platformService: PlatformService): GlobalConfig<any> {
	const {
		brandName,
		jurisdictionClientConfiguration,
		activeExperiments,
		appContext: {
			// TODO: OBGB-12018 remove device type and device experience
			environment,
			isDebug,
			version,
			deviceType,
			deviceExperience,
		},
	} = context;

	const configBase = platformService.isBrowser
		? context.config
		: merge({}, context.config, context.ssrClientConfiguration);

	if (!context.device) {
		context.device = {
			deviceType: DeviceType.Desktop,
			deviceExperience: DeviceExperience.Web,
		};
	}

	const devicePlatform = context.device!.deviceType || DeviceType.Desktop;
	const isNative = context.device!.deviceExperience === DeviceExperience.Native;
	const platform = isNative ? DeviceExperience.Native : devicePlatform;

	const experimentDimensions =
		activeExperiments && platformService.isBrowser
			? activeExperiments.filter((activeExperiment) => activeExperiment !== "ssrClientConfiguration")
			: activeExperiments;

	const featureFlagDimensions = getActiveDimensions(context.optimizelyFeatureFlags);

	return {
		languageCode: context.market.languageCode,
		appContext: context.config,
		prefetchData: context.prefetchData || {},
		config: configBase,
		tenant: brandName,
		device: context.device,
		jurisdiction: Jurisdiction.Mga.toLowerCase(),
		dimensions: uniq([...experimentDimensions, ...featureFlagDimensions]),
		configBase: {
			name: "combined",
			version: "combined",
			environment,
			tenants: {
				[`${brandName}`]: {
					...configBase,
					jurisdiction: jurisdictionClientConfiguration,
				},
			},
		},
		environmentContext: {
			// TODO: OBGB-12018 - to remove device type and device experience
			deviceType,
			deviceExperience,
			environment,
			isDebug,
			version,
		},
		initialTelemetryData: {
			Platform: platform,
		},
	};
}

/** @internal */
export function _optimizelyFeatureFlagsFactory() {
	const featureFlags$ = new BehaviorSubject<Record<string, any> | undefined>(undefined);

	return function (context: AppStartupContext): Record<string, any> {
		featureFlags$.next(context.optimizelyFeatureFlags);

		return {
			featureFlags: context.optimizelyFeatureFlags ?? {},
			featureFlags$,
		};
	};
}

/** @internal */
export function _routerConfigFactory(
	{ disabledRoutes, appContext, routes: localizedRoutes }: AppStartupContext,
	platformService: PlatformService
): RouterConfig {
	return {
		unknownRoute: CORE_ROUTE.notFound,
		defaultFallbackRoute: CORE_ROUTE.home,
		localizedRoutes,
		enableTracing: () => platformService.isBrowser && appContext.isDebug && !globalThis.isDOMSSR,
		disabledRoutes,
	};
}

/** @internal */
export function _coreConfigFactory({
	isBot,
	isPrimaryDomain,
	blockDomainMarkets,
	alternateDomains,
	isCrawlableForCurrentMarket,
	activeExperiments,
}: AppStartupContext): CoreConfig {
	return {
		isBot,
		isPrimaryDomain,
		blockDomainMarkets,
		alternateDomains,
		isCrawlableForCurrentMarket,
		activeExperiments,
	};
}
